import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { stravaService, StravaTokens, StravaAthlete, StravaActivity } from '../services/strava'

interface StravaContextType {
  isConnected: boolean
  athlete: StravaAthlete | null
  activities: StravaActivity[]
  isLoading: boolean
  error: string | null
  connectStrava: () => void
  disconnectStrava: () => void
  loadActivities: () => Promise<void>
  handleOAuthCallback: (code: string) => Promise<void>
}

const StravaContext = createContext<StravaContextType | undefined>(undefined)

export const useStrava = () => {
  const context = useContext(StravaContext)
  if (context === undefined) {
    throw new Error('useStrava must be used within a StravaProvider')
  }
  return context
}

interface StravaProviderProps {
  children: ReactNode
}

export const StravaProvider: React.FC<StravaProviderProps> = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false)
  const [athlete, setAthlete] = useState<StravaAthlete | null>(null)
  const [activities, setActivities] = useState<StravaActivity[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    // Check if already connected on mount
    const connected = stravaService.isConnected()
    if (connected) {
      const athleteData = stravaService.getAthlete()
      setIsConnected(true)
      setAthlete(athleteData)
    }
  }, [])

  const connectStrava = () => {
    const authUrl = stravaService.getAuthUrl()
    window.location.href = authUrl
  }

  const disconnectStrava = () => {
    stravaService.clearTokens()
    setIsConnected(false)
    setAthlete(null)
    setActivities([])
    setError(null)
  }

  const loadActivities = async () => {
    if (!isConnected) return

    setIsLoading(true)
    setError(null)

    try {
      const accessToken = await stravaService.getValidAccessToken()
      if (!accessToken) {
        throw new Error('No Strava tokens found')
      }

      const hikingActivities = await stravaService.getHikingActivities(accessToken)
      setActivities(hikingActivities)
    } catch (err) {
      // If the refresh token was revoked, reflect the disconnected state in the UI.
      if (!stravaService.isConnected()) {
        setIsConnected(false)
        setAthlete(null)
      }
      setError(err instanceof Error ? err.message : 'Failed to load activities')
    } finally {
      setIsLoading(false)
    }
  }

  const handleOAuthCallback = async (code: string) => {
    setIsLoading(true)
    setError(null)

    try {
      const tokens = await stravaService.exchangeToken(code)
      stravaService.storeTokens(tokens)
      setIsConnected(true)
      setAthlete(tokens.athlete)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to connect to Strava')
    } finally {
      setIsLoading(false)
    }
  }

  const value = {
    isConnected,
    athlete,
    activities,
    isLoading,
    error,
    connectStrava,
    disconnectStrava,
    loadActivities,
    handleOAuthCallback
  }

  return <StravaContext.Provider value={value}>{children}</StravaContext.Provider>
}